import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { computeTotals, SALES_DEFAULTS, type DocumentLineInput } from '@goyone/shared';
import { api } from '../../lib/api';
import { money } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { PackagePicker } from './PackagePicker';

export interface EditableLine {
  serviceId: string | null;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  discount: string;
}

export const emptyLine = (): EditableLine => ({
  serviceId: null,
  description: '',
  quantity: '1',
  unit: 'خدمة',
  unitPrice: '',
  discount: '',
});

interface ServiceOption {
  id: string;
  name: string;
  unit: string;
  unitPrice: string;
  /** السعرُ بعد خصم الكتالوج النافذ — يأتي من الخادم محسوبًا. */
  effectivePrice?: number;
  discountActive?: boolean;
}

/** يحوّل البنود المحرَّرة إلى الصيغة التي يقبلها الخادم. */
export function toPayload(lines: EditableLine[]): DocumentLineInput[] {
  return lines
    .filter((l) => l.description.trim() && Number(l.unitPrice) >= 0)
    .map((l) => ({
      serviceId: l.serviceId,
      description: l.description.trim(),
      quantity: Number(l.quantity) || 0,
      unit: l.unit || 'خدمة',
      unitPrice: Number(l.unitPrice) || 0,
      discount: Number(l.discount) || 0,
    }));
}

/**
 * محرّر بنود المستند.
 *
 * الإجماليات تُحسب بالدالة نفسها التي يستعملها الخادم، من الحزمة المشتركة.
 * لو كُرّر المنطق هنا لاختلف يومًا ما عن الخادم، فيرى المستخدم رقمًا ويُحفظ
 * غيره. الخادم يبقى هو المرجع على أي حال، لكن التطابق يمنع المفاجأة.
 */
export function LineEditor({
  lines,
  onChange,
  discount,
  onDiscountChange,
  taxRate,
  onTaxRateChange,
  disabled = false,
}: {
  lines: EditableLine[];
  onChange: (lines: EditableLine[]) => void;
  discount: string;
  onDiscountChange: (v: string) => void;
  taxRate: string;
  onTaxRateChange: (v: string) => void;
  disabled?: boolean;
}) {
  const [picking, setPicking] = useState(false);

  const services = useQuery({
    queryKey: ['services', 'options'],
    queryFn: () =>
      api<{ items: ServiceOption[] }>('/sales/services?pageSize=100&isActive=true'),
  });

  const totals = computeTotals(toPayload(lines), {
    headerDiscount: Number(discount) || 0,
    taxRate: Number(taxRate) || 0,
  });

  const patch = (index: number, changes: Partial<EditableLine>) => {
    onChange(lines.map((line, i) => (i === index ? { ...line, ...changes } : line)));
  };

  /**
   * اختيار خدمة من الكتالوج يملأ الوصف والسعر والوحدة.
   *
   * والسعرُ هو النافذُ اليوم لا المكتوبُ في الكتالوج: خدمةٌ عليها خصمٌ
   * ساري المدّة تدخل بسعرها بعد الخصم. وإدخالُ السعر الأصليّ كان يوجب
   * على البائع أن يحسم بيده في كلّ بند، فيُنسى مرّةً ويُضاعَف مرّة.
   *
   * والخصمُ يُطبَّق على سعر الوحدة لا في خانة «خصم البند»: الخانةُ مبلغٌ
   * ثابتٌ لا يتبع الكمّية، فلو كُتب فيها لصار خصمُ عشر نسخٍ كخصم واحدة.
   */
  const applyService = (index: number, serviceId: string) => {
    const service = services.data?.items.find((s) => s.id === serviceId);
    if (!service) {
      patch(index, { serviceId: null });
      return;
    }
    const price =
      service.effectivePrice != null
        ? Number(service.effectivePrice)
        : Number(service.unitPrice);
    patch(index, {
      serviceId: service.id,
      description: service.name,
      unit: service.unit,
      unitPrice: String(price),
    });
  };

  return (
    <div>
      <div className="table-wrap">
        <table className="table lines-table">
          <thead>
            <tr>
              <th style={{ width: '32%' }}>البند</th>
              <th style={{ width: 90 }}>الكمية</th>
              <th style={{ width: 90 }}>الوحدة</th>
              <th style={{ width: 120 }}>سعر الوحدة</th>
              <th style={{ width: 110 }}>خصم البند</th>
              <th style={{ width: 120 }}>الإجمالي</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={index}>
                <td>
                  <div className="col" style={{ gap: 4 }}>
                    <select
                      className="select"
                      value={line.serviceId ?? ''}
                      onChange={(e) => applyService(index, e.target.value)}
                      disabled={disabled}
                      aria-label="اختيار من الكتالوج"
                    >
                      <option value="">بند مخصّص</option>
                      {services.data?.items.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <input
                      className="input"
                      value={line.description}
                      onChange={(e) => patch(index, { description: e.target.value })}
                      placeholder="وصف البند"
                      disabled={disabled}
                      aria-label="وصف البند"
                    />
                  </div>
                </td>
                <td>
                  <input
                    className="input num"
                    type="number"
                    min="0"
                    step="any"
                    value={line.quantity}
                    onChange={(e) => patch(index, { quantity: e.target.value })}
                    disabled={disabled}
                    aria-label="الكمية"
                  />
                </td>
                <td>
                  <input
                    className="input"
                    value={line.unit}
                    onChange={(e) => patch(index, { unit: e.target.value })}
                    disabled={disabled}
                    aria-label="الوحدة"
                  />
                </td>
                <td>
                  <input
                    className="input num"
                    type="number"
                    min="0"
                    step="any"
                    value={line.unitPrice}
                    onChange={(e) => patch(index, { unitPrice: e.target.value })}
                    disabled={disabled}
                    aria-label="سعر الوحدة"
                  />
                </td>
                <td>
                  <input
                    className="input num"
                    type="number"
                    min="0"
                    step="any"
                    value={line.discount}
                    onChange={(e) => patch(index, { discount: e.target.value })}
                    disabled={disabled}
                    aria-label="خصم البند"
                  />
                </td>
                <td className="num strong">{money(totals.lineTotals[index] ?? 0)}</td>
                <td>
                  {lines.length > 1 && !disabled && (
                    <button
                      type="button"
                      className="btn btn-icon btn-ghost"
                      onClick={() => onChange(lines.filter((_, i) => i !== index))}
                      aria-label="حذف البند"
                    >
                      <Icon name="trash" size={15} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!disabled && (
        <div className="row mt-2" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => onChange([...lines, emptyLine()])}
          >
            <Icon name="plus" size={14} />
            إضافة بند
          </button>
          <button type="button" className="btn btn-sm" onClick={() => setPicking(true)}>
            <Icon name="layers" size={14} />
            إدراج باقة
          </button>
        </div>
      )}

      {picking && (
        <PackagePicker
          onClose={() => setPicking(false)}
          onInsert={(inserted) => {
            /*
             * البند الفارغ الافتتاحي يُستبدل لا يُترك.
             *
             * تركه ينتج مستندًا أول سطر فيه خالٍ، فيراه المستخدم خطأً ويحذفه
             * يدويًا في كل مرة يبدأ بإدراج باقة.
             */
            const existing = lines.filter((l) => l.description.trim() || l.unitPrice);
            onChange([...existing, ...inserted]);
          }}
        />
      )}

      <div className="totals-box mt-4">
        <div className="totals-row">
          <span>المجموع</span>
          <span className="num">{money(totals.subtotal)}</span>
        </div>

        <div className="totals-row">
          <label htmlFor="doc-discount">خصم على المستند</label>
          <input
            id="doc-discount"
            className="input num"
            type="number"
            min="0"
            step="any"
            value={discount}
            onChange={(e) => onDiscountChange(e.target.value)}
            disabled={disabled}
            style={{ width: 120 }}
          />
        </div>

        <div className="totals-row">
          <label htmlFor="doc-tax">
            الضريبة
            <span className="tiny mute"> (نسبة مئوية)</span>
          </label>
          <div className="row" style={{ gap: 6 }}>
            <input
              id="doc-tax"
              className="input num"
              type="number"
              min="0"
              max="100"
              step="any"
              value={taxRate}
              onChange={(e) => onTaxRateChange(e.target.value)}
              disabled={disabled}
              style={{ width: 70 }}
            />
            <span className="num mute">{money(totals.taxAmount)}</span>
          </div>
        </div>

        <div className="totals-row totals-final">
          <span>الإجمالي</span>
          <span className="num">{money(totals.total, SALES_DEFAULTS.currencyLabel)}</span>
        </div>
      </div>
    </div>
  );
}
